// src/components/Checkout/PaymentOptions.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";

import TopNavbar          from "../Nav/TopNavbar";
import Footer             from "./Footer";
import { makePostRequest } from "../../services/httpHelper";
import ErrorHandler        from "../../services/ErrorHandler";

export default function PaymentOptions() {
  /* -------- context -------- */
   const { state } = useLocation();          // order passed via navigate()
    const order = state;
  const navigate         = useNavigate();

  /* -------- local state -------- */
  const [method, setMethod]   = useState("");
  const [years , setYears]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [serverErr, setErr]   = useState(null);

  /* -------- available channels -------- */
  const methods = [
    { key: "card",   label: "Credit / Debit Card" },
    //{ key: "bank",   label: "Bank Transfer"      },
    { key: "airtel", label: "Mobile Money"  },
  ];

  /* ========================================================
     Submit → POST /payments/init  (backend Paystack service)
     ====================================================== */
  const handleCheckout = async () => {
    setErr(null);
    setLoading(true);

    try {
      const res = await makePostRequest("payments/init", {
        order_id: order.orderId,
        method,
        years,
      });

      if (res?.errorType) {
        // wrapped axios error from helper
        setErr(ErrorHandler(res).message);
        return;
      }

      /* ----------------------------------------------------
         Back-end contract:
           { authorization_url, reference }  –– Paystack
         OR { wait: true, reference }         –– manual polling
      ---------------------------------------------------- */
      const { checkout_url, reference, wait } = res.data;


      if (checkout_url) {
        // redirect customer to Paystack Hosted Page
        window.location.href = checkout_url;
      } else if (wait) {
        navigate("/waiting-payment", {
          state: { orderId: order.id, reference, method },
        });
      } else {
        setErr("Unexpected response from server.");
      }
    } catch (e) {
      setErr(e?.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------- render -------------------------- */
  const oneTime = ["solo", "team"].includes(order.product.toLowerCase());

  return (
    <>
      <TopNavbar />
      <Wrapper>
        <Box>
          <h2>Choose Payment Method</h2>

          {serverErr && <ErrorMsg>{serverErr}</ErrorMsg>}

          {/* ---- radio list ---- */}
          <Options>
            {methods.map((m) => (
              <Option
                key={m.key}
                className={method === m.key ? "selected" : ""}
                onClick={() => setMethod(m.key)}
              >
                <input
                  type="radio"
                  id={m.key}
                  checked={method === m.key}
                  onChange={() => setMethod(m.key)}
                />
                <label htmlFor={m.key}>{m.label}</label>
              </Option>
            ))}
          </Options>

          {/* ---- subscription years ---- */}
          {!oneTime && (
            <YearRow>
              <label htmlFor="years">Years:</label>
              <select
                id="years"
                value={years}
                onChange={(e) => setYears(+e.target.value)}
              >
                {[1, 2, 3, 4, 5].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </YearRow>
          )}

          {/* ---- pay button ---- */}
          <PayButton
            disabled={!method || loading}
            onClick={handleCheckout}
          >
            {loading
              ? "Redirecting…"
              : method
              ? `Pay with ${methods.find((m) => m.key === method)?.label}`
              : "Select a method"}
          </PayButton>
        </Box>
      </Wrapper>
      <Footer />
    </>
  );
}

/* ============== styled ============== */

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f7f9fc;
  padding: 24px;
`;

const Box = styled.div`
  background: #ffffff;
  padding: 32px;
  width: 100%;
  max-width: 480px;
  border-radius: 12px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.06);
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 24px 0;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: 2px solid #d0d0d0;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.25s;

  input { accent-color: #4b0082; cursor: pointer; }
  label { cursor: pointer; user-select: none; }

  &.selected {
    border-color: #4b0082;
    background  : #f3effc;
  }
  &:hover { background: #fafafa; }
`;

const YearRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  select {
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid #ccc;
  }
`;

const PayButton = styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 8px;
  background: #4b0082;
  color: #fff;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.25s;

  &:hover   { background: #3a006b; }
  &:disabled{
    background: #9b9b9b;
    cursor    : not-allowed;
  }
`;

const ErrorMsg = styled.p`
  color: #d83434;
  background: #fff2f2;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 18px;
  font-size: 14px;
`;
